import { useState, useRef, DragEvent, useMemo } from "react";
import {
  Upload,
  AlertTriangle,
  Check,
  GitMerge,
  UserPlus,
  AlertCircle,
  Copy,
  ShieldAlert,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ScrollArea } from "@/components/ui/scroll-area";

const LEMBRA_FIELDS = [
  { key: "name", label: "Full name", required: true },
  { key: "email", label: "Email", required: true },
  { key: "birthday", label: "Birthday", required: false },
  { key: "start_date", label: "Start date", required: false },
  { key: "department", label: "Department", required: false },
  { key: "dietary_requirements", label: "Dietary preferences", required: false },
  // Allergies are deliberately separate from dietary_requirements.
  // Conflating "I don't eat meat" with "peanuts will hospitalise me"
  // is a safety risk we won't take. The UI labels this as medical
  // and the team-card display treats it visually distinct (coral
  // warning badge instead of the neutral sky blue used for dietary).
  { key: "allergies", label: "Allergies (medical)", required: false },
  { key: "office_location", label: "Office location", required: false },
] as const;

type LembraFieldKey = (typeof LEMBRA_FIELDS)[number]["key"];

const AUTO_MAP: Record<string, LembraFieldKey> = {
  name: "name",
  "full name": "name",
  full_name: "name",
  "first name": "name",
  employee: "name",
  "employee name": "name",
  email: "email",
  "email address": "email",
  "e-mail": "email",
  birthday: "birthday",
  dob: "birthday",
  "date of birth": "birthday",
  "birth date": "birthday",
  birthdate: "birthday",
  "start date": "start_date",
  start_date: "start_date",
  "start-date": "start_date",
  "date started": "start_date",
  "hire date": "start_date",
  joined: "start_date",
  department: "department",
  dept: "department",
  team: "department",
  // Dietary = preferences, not medical. Note "allergies" is no longer
  // here, it now maps to its own field below. Existing CSVs whose
  // first column header was literally "Allergies" will land in the
  // right place; CSVs with "Dietary, Allergies" as separate columns
  // will also disambiguate correctly.
  dietary: "dietary_requirements",
  "dietary requirements": "dietary_requirements",
  "dietary preferences": "dietary_requirements",
  dietary_requirements: "dietary_requirements",
  diet: "dietary_requirements",
  "food preferences": "dietary_requirements",
  "food requirements": "dietary_requirements",
  // Allergies, medical. Decoupled from dietary in the
  // 20260430030000 migration.
  allergies: "allergies",
  allergy: "allergies",
  allergens: "allergies",
  "food allergies": "allergies",
  "medical allergies": "allergies",
  office: "office_location",
  "office location": "office_location",
  office_location: "office_location",
  location: "office_location",
};

export interface ImportedMember {
  name: string;
  email: string;
  birthday: string;
  start_date: string;
  department: string;
  dietary_requirements: string;
  // Semicolon-separated string of allergies, mirrors the
  // dietary_requirements CSV format. Distinct field; never merged
  // into dietary by accident.
  allergies: string;
  office_location: string;
}

/**
 * The shape of an existing team_members row that the dialog needs to
 * detect duplicates and decide what fields to merge. Caller (Team.tsx)
 * pulls this from the live members query and passes it in unchanged —
 * we don't refetch inside the dialog so the merge analysis can't get
 * out of sync with the team page's current view.
 */
export interface ExistingMemberLite {
  id: string;
  name: string;
  email: string | null;
  birthday: string | null;
  start_date: string | null;
  department: string | null;
  office_location: string | null;
  dietary_requirements: string[] | null;
  allergies: string[] | null;
  data_source?: "admin_seeded" | "user_csv" | "user_manual" | null;
}

/**
 * Outcome of the dedupe analysis. The dialog hands this object to
 * the caller's onImport, by then, every CSV row has been classified
 * as new, merge, or conflict. The caller does the actual DB writes
 * (insert for `toInsert`, update for `toUpdate`) so all auth/RLS
 * concerns stay in the page that owns the team data.
 *
 * Conflicts are non-blocking: they're rows where the CSV has a
 * different value for a field that already had one. We keep the
 * existing value (don't auto-overwrite) and surface the diff to
 * the user so they can resolve it manually after import.
 */
export interface ImportAnalysis {
  toInsert: ImportedMember[];
  toUpdate: Array<{
    id: string;
    existing: ExistingMemberLite;
    incoming: ImportedMember;
    fieldsFilled: string[];
    /**
     * Conflict resolution overrides for this row. Keys are field
     * names from the conflicts list; presence means "use the CSV
     * value, overwrite the existing one". The caller should add
     * each overridden field to its UPDATE payload.
     */
    overrides: Record<string, string>;
  }>;
  conflicts: Array<{
    existingId: string;
    name: string;
    field: string;
    existingValue: string;
    csvValue: string;
    /**
     * Stable identifier for matching this conflict against the user's
     * resolution choices in state. `${existingId}::${field}` since
     * a member can have multiple field conflicts but only one per
     * field.
     */
    key: string;
  }>;
  /**
   * Rows in the uploaded CSV whose email duplicates an EARLIER row in
   * the same upload. We keep the first occurrence and surface the
   * dropped ones so the user knows their CSV had repeats. Critical
   * because the partial unique index on (company_id, lower(email))
   * would otherwise reject the entire .insert() batch on the first
   * duplicate, surfacing as an opaque "Failed to import" error.
   */
  inCsvDuplicates: Array<{
    email: string;
    name: string;
    /** 1-indexed CSV row number for "Row 7 duplicates row 3" messaging. */
    droppedAt: number;
    keptAt: number;
  }>;
  unchanged: number;
}

interface ImportCsvDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onImport: (analysis: ImportAnalysis) => void | Promise<void>;
  /**
   * Existing team members in this scope (company). Used to detect
   * duplicates by lowercased email and merge missing fields rather
   * than creating duplicates. Pass an empty array to skip merging.
   */
  existingMembers: ExistingMemberLite[];
}

type Step = "upload" | "map" | "merge" | "review";

/**
 * Detect the most likely field delimiter for a CSV-shaped text blob.
 * Scans the first non-empty line and counts each candidate delimiter,
 * picking whichever appears most often. Catches:
 *
 *   - Standard CSV (`,`)
 *   - European Excel exports (`;`)
 *   - TSV (`\t`)
 *   - Pipe-delimited dumps (`|`)
 *
 * Falls back to comma if no candidate appears at all (single-column
 * file or empty input). Quoted-field aware only in the trivial sense
 *, we count delimiters in the raw line, but a comma inside a quoted
 * field would inflate the count. For the common case (consistent
 * delimiter across columns) this is fine; pathological inputs would
 * just need a manual delimiter override (a future improvement).
 */
function detectDelimiter(text: string): string {
  const candidates = [",", ";", "\t", "|"];
  // Strip BOM before sampling so the count isn't thrown off.
  const sample = text.charCodeAt(0) === 0xfeff ? text.slice(1) : text;
  const firstLine = sample.split(/\r?\n/).find((l) => l.trim()) ?? "";
  let best = ",";
  let bestCount = 0;
  for (const d of candidates) {
    let count = 0;
    for (const ch of firstLine) {
      if (ch === d) count++;
    }
    if (count > bestCount) {
      best = d;
      bestCount = count;
    }
  }
  return bestCount > 0 ? best : ",";
}

/**
 * RFC 4180-compliant CSV parser. Three correctness wins over the
 * naive split-on-newline approach:
 *
 *   1. Quoted fields can contain newlines. Excel-exported CSVs and
 *      free-form notes columns regularly produce these. The previous
 *      parser would split such a field across multiple "rows" and
 *      mangle data silently, a real safety risk if it splits a
 *      dietary or allergy entry mid-token.
 *
 *   2. UTF-8 BOM (U+FEFF) at the start of the file. Excel adds this
 *      when "Save as CSV (UTF-8)" is used. The old parser would
 *      prefix the first header column with the BOM character, breaking
 *      every auto-map for that column.
 *
 *   3. Pluggable delimiter (caller passes from detectDelimiter).
 *      Lets us handle European semicolon CSVs and TSV exports without
 *      a separate code path.
 *
 * The state machine is one pass over the full text. Newlines are only
 * treated as record separators when we're not inside quotes. \r\n is
 * collapsed to a single line break.
 */
function parseCsvRaw(text: string, delimiter: string = ","): string[][] {
  // BOM strip, Excel/Numbers UTF-8 export prepends U+FEFF and the
  // previous parser would carry it into the first header.
  if (text.charCodeAt(0) === 0xfeff) {
    text = text.slice(1);
  }

  const rows: string[][] = [];
  let current: string[] = [];
  let field = "";
  let inQuotes = false;

  for (let i = 0; i < text.length; i++) {
    const ch = text[i];

    if (inQuotes) {
      if (ch === '"' && text[i + 1] === '"') {
        field += '"';
        i++; // skip the second quote of the escape pair
      } else if (ch === '"') {
        inQuotes = false;
      } else {
        // Note: newlines are accepted inside quotes, that's the
        // whole point of this rewrite.
        field += ch;
      }
      continue;
    }

    // Outside quotes
    if (ch === '"' && field === "") {
      // Opening quote. We only honour quotes that start a field;
      // a quote mid-field is treated as literal data (the prior
      // behaviour) so we don't break weirdly-formatted CSVs.
      inQuotes = true;
    } else if (ch === delimiter) {
      current.push(field.trim());
      field = "";
    } else if (ch === "\n" || ch === "\r") {
      // End of record. Collapse \r\n into a single boundary so we
      // don't emit an empty row on Windows-formatted files.
      if (ch === "\r" && text[i + 1] === "\n") i++;
      // Skip records that are purely empty (e.g., trailing newline
      // at end of file) so callers still see "header + 1 row" min.
      if (current.length > 0 || field !== "") {
        current.push(field.trim());
        rows.push(current);
      }
      current = [];
      field = "";
    } else {
      field += ch;
    }
  }

  // Final record, file might end without a trailing newline.
  if (current.length > 0 || field !== "") {
    current.push(field.trim());
    rows.push(current);
  }

  return rows;
}

/**
 * Levenshtein edit distance between two short strings. Used by the
 * fuzzy header matcher below. Cap-aware: returns early once the
 * running cost exceeds the caller's allowed budget, which makes
 * "is this within distance 2?" cheap even on dictionaries with many
 * entries.
 */
function levenshtein(a: string, b: string, max: number): number {
  if (a === b) return 0;
  if (Math.abs(a.length - b.length) > max) return max + 1;
  const al = a.length;
  const bl = b.length;
  // Two-row dynamic programming. We don't need the full matrix
  // because we only care about the final cell.
  let prev: number[] = Array(bl + 1)
    .fill(0)
    .map((_, i) => i);
  let curr: number[] = Array(bl + 1).fill(0);
  for (let i = 1; i <= al; i++) {
    curr[0] = i;
    let rowMin = curr[0];
    for (let j = 1; j <= bl; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      curr[j] = Math.min(
        prev[j] + 1, // deletion
        curr[j - 1] + 1, // insertion
        prev[j - 1] + cost, // substitution
      );
      if (curr[j] < rowMin) rowMin = curr[j];
    }
    // Early exit if every cell on this row is over budget.
    if (rowMin > max) return max + 1;
    [prev, curr] = [curr, prev];
  }
  return prev[bl];
}

/**
 * Match a CSV header against AUTO_MAP, with a Levenshtein fallback.
 *
 * The exact-match path catches everything we have a synonym for.
 * The fuzzy path is the safety net for typos ("birthay", "fulnme")
 * and minor variants ("first names" vs "first name") that aren't
 * worth enumerating in AUTO_MAP. We require a minimum candidate-key
 * length of 4 to avoid silly matches like "team" → "name" (distance
 * 3 from "name") or single-char headers fuzzy-mapping to anything.
 *
 * Returns:
 *   - { kind: 'exact', field } for an exact AUTO_MAP hit
 *   - { kind: 'fuzzy', field, source } for a fuzzy hit (so the UI
 *     can flag it as "did you mean...?")
 *   - null when nothing matches within the edit-distance budget
 */
type FuzzyMatch =
  | { kind: "exact"; field: LembraFieldKey }
  | { kind: "fuzzy"; field: LembraFieldKey; source: string };

function matchHeader(rawHeader: string): FuzzyMatch | null {
  const normalized = rawHeader.toLowerCase().trim();
  if (!normalized) return null;
  const exact = AUTO_MAP[normalized];
  if (exact) return { kind: "exact", field: exact };

  // Fuzzy fallback: only attempt when the input is long enough that
  // an edit-distance-2 match isn't trivially noisy.
  if (normalized.length < 4) return null;

  let best: { key: string; distance: number } | null = null;
  // Tighter budget for shorter strings, distance 2 on a 4-char
  // input is half the string, which is too aggressive.
  const budget = normalized.length <= 5 ? 1 : 2;

  for (const candidate of Object.keys(AUTO_MAP)) {
    if (candidate.length < 4) continue;
    const d = levenshtein(normalized, candidate, budget);
    if (d <= budget && (!best || d < best.distance)) {
      best = { key: candidate, distance: d };
      if (d === 0) break; // can't beat exact (shouldn't happen here, but cheap to check)
    }
  }

  if (!best) return null;
  return { kind: "fuzzy", field: AUTO_MAP[best.key], source: best.key };
}

function detectDateFormat(values: string[]): "dmy" | "mdy" {
  for (const v of values) {
    const parts = v.split(/[/\-.]/);
    if (parts.length === 3) {
      const first = parseInt(parts[0], 10);
      if (first > 12) return "dmy";
    }
  }
  return "dmy"; // default Australian
}

function parseDate(value: string, format: "dmy" | "mdy"): string {
  if (!value) return "";
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
  const parts = value.split(/[/\-.]/);
  if (parts.length !== 3) return "";
  let day: string, month: string, year: string;
  if (format === "dmy") {
    [day, month, year] = parts;
  } else {
    [month, day, year] = parts;
  }
  if (year.length === 2) year = `20${year}`;
  return `${year.padStart(4, "0")}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
}

interface RowError {
  row: number;
  fields: string[];
}

/**
 * Build the merge analysis. For each CSV row that has an email,
 * look up an existing member with the same lowercased email and:
 *   - missing (existing field is null/empty) → fill it (toUpdate)
 *   - same as existing → no-op (counted as unchanged)
 *   - different from existing → conflict (preserve existing, flag it)
 * Rows whose email has no existing match become inserts.
 *
 * Empty-email CSV rows always become inserts since we have nothing
 * to dedupe against.
 */
function buildAnalysis(
  members: ImportedMember[],
  existing: ExistingMemberLite[],
): ImportAnalysis {
  const byEmail = new Map<string, ExistingMemberLite>();
  for (const m of existing) {
    if (m.email) byEmail.set(m.email.toLowerCase(), m);
  }

  const toInsert: ImportAnalysis["toInsert"] = [];
  const toUpdate: ImportAnalysis["toUpdate"] = [];
  const conflicts: ImportAnalysis["conflicts"] = [];
  const inCsvDuplicates: ImportAnalysis["inCsvDuplicates"] = [];
  let unchanged = 0;

  // Track which emails we've already classified within THIS upload so
  // a CSV with two rows for jane@acme.com doesn't end up with both
  // queued for insert (the partial unique index on
  // (company_id, lower(email)) would reject the entire .insert() batch
  // on the duplicate, surfacing as an opaque "Failed to import" toast).
  // First occurrence wins; later ones go to inCsvDuplicates so the
  // user can see the dropped rows in the merge UI.
  const seenInCsv = new Map<string, { row: number; name: string }>();

  members.forEach((incoming, i) => {
    const key = incoming.email.toLowerCase();
    // 1-indexed because spreadsheet apps and humans count from 1.
    // We add 2 here (1 for header row, 1 for 0-index → 1-index).
    const csvRow = i + 2;

    if (key) {
      const earlier = seenInCsv.get(key);
      if (earlier) {
        inCsvDuplicates.push({
          email: incoming.email,
          name: incoming.name,
          droppedAt: csvRow,
          keptAt: earlier.row,
        });
        return; // skip, first occurrence already handled below
      }
      seenInCsv.set(key, { row: csvRow, name: incoming.name });
    }

    const match = key ? byEmail.get(key) : null;

    if (!match) {
      toInsert.push(incoming);
      return;
    }

    // Build a per-field comparison. We only fill missing fields and
    // never overwrite a present one, overwrites become conflicts
    // for the user to resolve. dietary_requirements and allergies are
    // lists, compared via semicolon-split.
    const fieldsFilled: string[] = [];
    const incomingDiet = incoming.dietary_requirements
      ? incoming.dietary_requirements.split(";").map((s) => s.trim()).filter(Boolean)
      : [];
    const incomingAllergies = incoming.allergies
      ? incoming.allergies.split(";").map((s) => s.trim()).filter(Boolean)
      : [];

    const existingHas = (v: unknown) =>
      v !== null && v !== undefined && (Array.isArray(v) ? v.length > 0 : String(v).trim() !== "");

    const considerScalar = (
      field: keyof Pick<ImportedMember, "birthday" | "start_date" | "department" | "office_location" | "name">,
      existingVal: string | null | undefined,
      incomingVal: string,
    ) => {
      if (!incomingVal) return;
      if (!existingHas(existingVal)) {
        fieldsFilled.push(field);
      } else if (existingVal !== incomingVal) {
        conflicts.push({
          existingId: match.id,
          name: match.name,
          field,
          existingValue: String(existingVal),
          csvValue: incomingVal,
          key: `${match.id}::${field}`,
        });
      }
    };

    considerScalar("name", match.name, incoming.name);
    considerScalar("birthday", match.birthday, incoming.birthday);
    considerScalar("start_date", match.start_date, incoming.start_date);
    considerScalar("department", match.department, incoming.department);
    considerScalar("office_location", match.office_location, incoming.office_location);

    if (incomingDiet.length > 0) {
      const existingDiet = match.dietary_requirements ?? [];
      // Diet merges by union, additive, never destructive. A CSV
      // adding "vegan" to someone who already had "gluten-free"
      // should land both, not replace one with the other.
      const merged = Array.from(new Set([...existingDiet, ...incomingDiet]));
      if (merged.length > existingDiet.length) {
        fieldsFilled.push("dietary_requirements");
      }
    }

    if (incomingAllergies.length > 0) {
      const existingAllergies = match.allergies ?? [];
      // Allergies merge identically to dietary (union, additive).
      // The merge is conservative because allergies are medical —
      // we never want to drop one that's already on file. If the
      // CSV omits a known allergy, that's a "human review needed"
      // case but we won't auto-remove the existing entries.
      const merged = Array.from(new Set([...existingAllergies, ...incomingAllergies]));
      if (merged.length > existingAllergies.length) {
        fieldsFilled.push("allergies");
      }
    }

    if (fieldsFilled.length > 0) {
      // overrides starts empty, gets filled in later by the merge
      // step UI when the user opts to take CSV values for specific
      // conflicts (M7). The dialog's onImport callback merges them
      // into this analysis right before handing to the caller.
      toUpdate.push({ id: match.id, existing: match, incoming, fieldsFilled, overrides: {} });
    } else {
      // Existing record fully covered; CSV row contributed nothing
      // novel. Counted so the user knows the import wasn't broken
      //, "5 already up to date" is a useful signal.
      unchanged++;
    }
  });

  return { toInsert, toUpdate, conflicts, inCsvDuplicates, unchanged };
}

export function ImportCsvDialog({ open, onOpenChange, onImport, existingMembers }: ImportCsvDialogProps) {
  const [step, setStep] = useState<Step>("upload");
  const [headers, setHeaders] = useState<string[]>([]);
  const [rows, setRows] = useState<string[][]>([]);
  const [mapping, setMapping] = useState<Record<number, LembraFieldKey | "skip">>({});
  // Track which auto-mappings were fuzzy (typo/synonym) vs exact.
  // The Apollo-style preview surfaces a "did you mean…?" hint on
  // fuzzy-mapped columns so the user spot-checks before importing.
  const [fuzzyMappings, setFuzzyMappings] = useState<Record<number, string>>({});
  const [dateFormat, setDateFormat] = useState<"dmy" | "mdy">("dmy");
  const [errors, setErrors] = useState<RowError[]>([]);
  const [skippedRows, setSkippedRows] = useState<Set<number>>(new Set());
  const [uploadError, setUploadError] = useState("");
  const [dragging, setDragging] = useState(false);
  const [importing, setImporting] = useState(false);
  // L3: detected delimiter for this CSV. Surfaced to the user so they
  // can see "we read this as a semicolon-separated file", useful when
  // a European Excel export looks weirdly all-in-one-column otherwise.
  const [delimiter, setDelimiter] = useState<string>(",");
  // L4: rows whose column count doesn't match the header. We tolerate
  // them at parse time (extra columns drop, missing columns become
  // empty), but flag the count so the user knows their file looks
  // ragged before they commit to importing.
  const [raggedRowCount, setRaggedRowCount] = useState(0);
  // M7: tracks which conflicts the user has chosen to override with
  // the CSV value. Keyed by the conflict's stable key
  // (`${existingId}::${field}`). Defaults to "keep existing" (key
  // absent); checkbox flips to "use CSV" (key present).
  const [conflictOverrides, setConflictOverrides] = useState<Set<string>>(new Set());
  const fileRef = useRef<HTMLInputElement>(null);

  const handleFile = (file: File) => {
    setUploadError("");
    if (!file.name.endsWith(".csv")) {
      setUploadError("Please upload a .csv file.");
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const text = e.target?.result as string;
        // L3: detect delimiter before parsing so semicolon/tab/pipe
        // CSVs land in their cells rather than collapsing into one
        // column. detectDelimiter only looks at the first non-empty
        // line so it's cheap.
        const detected = detectDelimiter(text);
        const parsed = parseCsvRaw(text, detected);
        if (parsed.length < 2) {
          setUploadError("CSV must have a header row and at least one data row.");
          return;
        }
        const hdrs = parsed[0];
        const dataRows = parsed.slice(1);
        // L4: count rows whose column count doesn't match the header.
        // We don't reject them, the rest of the pipeline tolerates
        // ragged input, but we surface the count so the user can
        // spot-check before importing.
        const ragged = dataRows.filter((r) => r.length !== hdrs.length).length;
        setHeaders(hdrs);
        setRows(dataRows);
        setDelimiter(detected);
        setRaggedRowCount(ragged);

        const autoMap: Record<number, LembraFieldKey | "skip"> = {};
        const fuzzy: Record<number, string> = {};
        const usedFields = new Set<LembraFieldKey>();
        // Two-pass auto-detection: exact matches first (higher
        // confidence), fuzzy matches only fill remaining columns.
        // This avoids a fuzzy "birthay" claiming the birthday slot
        // before an exact "Birthday" column further along.
        hdrs.forEach((h, i) => {
          const m = matchHeader(h);
          if (m?.kind === "exact" && !usedFields.has(m.field)) {
            autoMap[i] = m.field;
            usedFields.add(m.field);
          }
        });
        hdrs.forEach((h, i) => {
          if (autoMap[i]) return;
          const m = matchHeader(h);
          if (m?.kind === "fuzzy" && !usedFields.has(m.field)) {
            autoMap[i] = m.field;
            usedFields.add(m.field);
            fuzzy[i] = m.source;
          }
        });
        setMapping(autoMap);
        setFuzzyMappings(fuzzy);

        const dateColIndices = Object.entries(autoMap)
          .filter(([, v]) => v === "birthday" || v === "start_date")
          .map(([k]) => parseInt(k, 10));
        if (dateColIndices.length > 0) {
          const sampleDates = dataRows
            .slice(0, 10)
            .flatMap((r) => dateColIndices.map((i) => r[i] || ""))
            .filter(Boolean);
          setDateFormat(detectDateFormat(sampleDates));
        }

        setStep("map");
      } catch {
        setUploadError("Failed to parse CSV. Please check the format.");
      }
    };
    reader.readAsText(file);
  };

  const onDrop = (e: DragEvent) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  };

  const reset = () => {
    setStep("upload");
    setHeaders([]);
    setRows([]);
    setMapping({});
    setFuzzyMappings({});
    setErrors([]);
    setSkippedRows(new Set());
    setUploadError("");
    setDragging(false);
    setDateFormat("dmy");
    setImporting(false);
    setDelimiter(",");
    setRaggedRowCount(0);
    setConflictOverrides(new Set());
  };

  const handleClose = (v: boolean) => {
    if (!v) reset();
    onOpenChange(v);
  };

  const updateMapping = (colIndex: number, value: string) => {
    setMapping((prev) => {
      const next = { ...prev };
      if (value === "skip" || value === "") {
        next[colIndex] = "skip";
      } else {
        Object.keys(next).forEach((k) => {
          if (next[parseInt(k, 10)] === value) {
            next[parseInt(k, 10)] = "skip";
          }
        });
        next[colIndex] = value as LembraFieldKey;
      }
      return next;
    });
  };

  const mappedFields = useMemo(() => {
    const set = new Set<string>();
    Object.values(mapping).forEach((v) => {
      if (v !== "skip") set.add(v);
    });
    return set;
  }, [mapping]);

  const missingRequired = LEMBRA_FIELDS.filter((f) => f.required && !mappedFields.has(f.key));

  const buildMembers = (): { members: ImportedMember[]; rowErrors: RowError[] } => {
    const members: ImportedMember[] = [];
    const rowErrors: RowError[] = [];
    const fieldToCol: Record<string, number> = {};
    Object.entries(mapping).forEach(([col, field]) => {
      if (field !== "skip") fieldToCol[field] = parseInt(col, 10);
    });

    rows.forEach((row, idx) => {
      if (skippedRows.has(idx)) return;
      const get = (key: string) => (fieldToCol[key] !== undefined ? (row[fieldToCol[key]] || "").trim() : "");
      const name = get("name");
      const email = get("email");
      const startRaw = get("start_date");
      const birthdayRaw = get("birthday");
      const missing: string[] = [];
      if (!name) missing.push("Full name");
      if (!email) missing.push("Email");
      if (missing.length > 0) {
        rowErrors.push({ row: idx, fields: missing });
        return;
      }
      members.push({
        name,
        email,
        birthday: parseDate(birthdayRaw, dateFormat),
        start_date: parseDate(startRaw, dateFormat),
        department: get("department"),
        dietary_requirements: get("dietary_requirements"),
        allergies: get("allergies"),
        office_location: get("office_location"),
      });
    });
    return { members, rowErrors };
  };

  const goToMerge = () => {
    const { rowErrors } = buildMembers();
    setErrors(rowErrors);
    setStep("merge");
  };

  const validMembers = useMemo(() => {
    if (step === "upload" || step === "map") return [];
    const { members } = buildMembers();
    return members;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, mapping, rows, skippedRows, dateFormat]);

  const analysis = useMemo<ImportAnalysis | null>(() => {
    if (step !== "merge" && step !== "review") return null;
    return buildAnalysis(validMembers, existingMembers);
  }, [step, validMembers, existingMembers]);

  const toggleSkip = (rowIdx: number) => {
    setSkippedRows((prev) => {
      const next = new Set(prev);
      if (next.has(rowIdx)) next.delete(rowIdx);
      else next.add(rowIdx);
      return next;
    });
    const { rowErrors } = buildMembers();
    setErrors(rowErrors);
  };

  const handleImport = async () => {
    if (!analysis) return;
    setImporting(true);
    try {
      // M7: bake the user's conflict-override choices into the
      // analysis right before handing it to the caller. For each
      // toUpdate entry we copy in the overrides for any conflict
      // matching that member id, so the caller can blindly trust
      // `update.overrides[field]` and apply it. Existing toUpdate
      // entries with no conflicts remain unaffected.
      const overridesByMember = new Map<string, Record<string, string>>();
      for (const c of analysis.conflicts) {
        if (!conflictOverrides.has(c.key)) continue;
        const existing = overridesByMember.get(c.existingId) ?? {};
        existing[c.field] = c.csvValue;
        overridesByMember.set(c.existingId, existing);
      }

      // Promote conflict-only members into toUpdate when the user
      // chose to override at least one of their fields. Without this,
      // a member whose ONLY change was a conflict-override would
      // never see its update fire (toUpdate is built from
      // fieldsFilled, which excludes conflicts by definition).
      const toUpdate = analysis.toUpdate.map((u) => ({
        ...u,
        overrides: overridesByMember.get(u.id) ?? {},
      }));
      const existingIds = new Set(toUpdate.map((u) => u.id));
      for (const [memberId, overrides] of overridesByMember.entries()) {
        if (existingIds.has(memberId)) continue;
        const conflict = analysis.conflicts.find((c) => c.existingId === memberId);
        if (!conflict) continue;
        // We don't have the full ExistingMemberLite/ImportedMember
        // pair handy here, but the caller's update path only needs
        // the id + overrides to write. Fabricate stubs for typing.
        toUpdate.push({
          id: memberId,
          existing: {
            id: memberId,
            name: conflict.name,
            email: "",
            birthday: null,
            start_date: null,
            department: null,
            office_location: null,
            dietary_requirements: null,
            allergies: null,
          },
          incoming: {
            name: "",
            email: "",
            birthday: "",
            start_date: "",
            department: "",
            dietary_requirements: "",
            allergies: "",
            office_location: "",
          },
          fieldsFilled: [],
          overrides,
        });
      }

      await onImport({ ...analysis, toUpdate });
      handleClose(false);
    } finally {
      setImporting(false);
    }
  };

  const toggleConflictOverride = (key: string) => {
    setConflictOverrides((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };
  const setAllConflictOverrides = (use: boolean) => {
    if (!analysis) return;
    setConflictOverrides(use ? new Set(analysis.conflicts.map((c) => c.key)) : new Set());
  };

  const previewRows = rows.slice(0, 3);

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent
        className="rounded-xl max-w-5xl max-h-[90dvh] overflow-y-auto"
        style={{ backgroundColor: "#FAF6F0" }}
      >
        <DialogHeader>
          <DialogTitle className="text-xl font-bold text-navy">
            {step === "upload" && "Import CSV"}
            {step === "map" && "Map columns"}
            {step === "merge" && "Review merge"}
            {step === "review" && "Final review"}
          </DialogTitle>
          <DialogDescription className="text-charcoal/70">
            {step === "upload" && "Upload a CSV file with your team members."}
            {step === "map" && "Match your CSV columns to Lembra fields. We've auto-detected what we can."}
            {step === "merge" && analysis &&
              `${analysis.toInsert.length} new · ${analysis.toUpdate.length} merged · ${analysis.conflicts.length} conflicts · ${analysis.unchanged} already up to date.`}
            {step === "review" && analysis &&
              `Importing ${analysis.toInsert.length} new and updating ${analysis.toUpdate.length}.`}
          </DialogDescription>
        </DialogHeader>

        {/* STEP 1: Upload */}
        {step === "upload" && (
          <div>
            <div
              className={`mt-4 border-2 border-dashed rounded-xl p-10 text-center cursor-pointer transition-colors ${
                dragging ? "border-butter bg-butter/20" : "border-sky bg-sky/10 hover:border-sky/80"
              }`}
              onDragOver={(e) => {
                e.preventDefault();
                setDragging(true);
              }}
              onDragLeave={() => setDragging(false)}
              onDrop={onDrop}
              onClick={() => fileRef.current?.click()}
            >
              <Upload className="mx-auto h-10 w-10 text-navy/50 mb-3" />
              <p className="text-navy font-medium">Drop your CSV here or click to browse</p>
              <p className="text-charcoal/50 text-sm mt-1">Only .csv files accepted</p>
            </div>
            <input
              ref={fileRef}
              type="file"
              accept=".csv"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) handleFile(f);
              }}
            />
            {uploadError && <p className="text-coral text-sm mt-3">{uploadError}</p>}
          </div>
        )}

        {/* STEP 2: Apollo-style preview + mapping.
            Each CSV column is a self-contained card showing its
            header, mapping dropdown, recognition badge, and 3 sample
            data values inline. Lets the user verify "First Name → Full
            name" by eyeballing actual data, if the column contains
            dates, the dropdown is wrong and they can fix it before
            anything is imported. Horizontally scrollable for CSVs
            with many columns. */}
        {step === "map" && (
          <div className="mt-4 space-y-5">
            <div className="flex flex-wrap items-center gap-4">
              <div>
                <p className="text-sm font-semibold text-navy">
                  {headers.length} columns detected
                </p>
                <p className="text-xs text-charcoal/60">
                  {mappedFields.size} mapped · {headers.length - mappedFields.size} skipped or unmapped
                  {Object.keys(fuzzyMappings).length > 0 &&
                    ` · ${Object.keys(fuzzyMappings).length} fuzzy match${
                      Object.keys(fuzzyMappings).length !== 1 ? "es" : ""
                    } (verify these)`}
                  {/* L3: tell the user how we read the file. Useful when
                      a European Excel export looks all-in-one-column
                      otherwise, they can see "we used semicolon" and
                      either re-export or trust the detection. */}
                  {delimiter !== "," && (
                    <>
                      {" · "}
                      Detected{" "}
                      <span className="font-semibold text-navy">
                        {delimiter === ";"
                          ? "semicolon-separated"
                          : delimiter === "\t"
                          ? "tab-separated"
                          : delimiter === "|"
                          ? "pipe-separated"
                          : "custom-separated"}
                      </span>
                    </>
                  )}
                </p>
              </div>
              <div className="ml-auto flex items-center gap-2">
                <span className="text-sm font-medium text-navy">Date format:</span>
                <Select value={dateFormat} onValueChange={(v) => setDateFormat(v as "dmy" | "mdy")}>
                  <SelectTrigger className="w-52">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="dmy">DD/MM/YYYY (Australian)</SelectItem>
                    <SelectItem value="mdy">MM/DD/YYYY (US)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* L4: ragged-row warning. Some rows have a column count
                different from the header, which usually means missing
                data or unescaped delimiters in a free-form column.
                We continue to import (downstream code tolerates
                ragged rows by treating extras as drops + missing as
                empty), but the user gets a heads-up to spot-check. */}
            {raggedRowCount > 0 && (
              <div className="rounded-lg border border-coral/30 bg-coral/5 p-3 flex items-start gap-2 text-sm">
                <AlertTriangle className="h-4 w-4 text-coral shrink-0 mt-0.5" />
                <div className="text-charcoal/80">
                  <strong className="text-coral">
                    {raggedRowCount} row{raggedRowCount !== 1 ? "s have" : " has"} a
                    different column count than the header.
                  </strong>{" "}
                  Usually means a value contained an unquoted{" "}
                  <code className="font-mono text-xs">{delimiter === "\t" ? "tab" : delimiter}</code>
                  {" "}or some columns are missing. Import will still proceed, extras are dropped, gaps stay empty, but spot-check the sample data below.
                </div>
              </div>
            )}

            <div className="rounded-lg border-2 border-charcoal/10 bg-white p-3 text-xs text-charcoal/70 flex items-start gap-2">
              <AlertCircle className="h-4 w-4 text-navy mt-0.5 shrink-0" />
              <p>
                Each column below shows where the data will land in Lembra plus 3 sample
                values from your file. If the dropdown looks wrong (e.g. a "First name"
                column has dates), pick the correct field before you continue. Required
                columns are marked with <span className="text-coral">*</span>.
              </p>
            </div>

            <div className="overflow-x-auto rounded-lg border border-charcoal/10">
              <div className="flex divide-x divide-charcoal/10 min-w-fit">
                {headers.map((h, i) => {
                  const current = mapping[i] || "skip";
                  const isMapped = current !== "skip";
                  const isFuzzy = !!fuzzyMappings[i];
                  const sample = previewRows.map((r) => r[i] ?? "");
                  return (
                    <div
                      key={i}
                      className={`flex flex-col w-[200px] shrink-0 ${
                        isFuzzy
                          ? "bg-butter/30"
                          : isMapped
                          ? "bg-white"
                          : "bg-charcoal/3"
                      }`}
                    >
                      <div className="p-3 border-b border-charcoal/8 space-y-2">
                        <div className="flex items-center gap-1.5 min-h-[20px]">
                          <p
                            className="font-mono text-xs font-semibold text-navy truncate flex-1"
                            title={h}
                          >
                            {h || <span className="italic text-charcoal/40">(empty)</span>}
                          </p>
                          {isMapped && !isFuzzy && (
                            <Check className="h-3.5 w-3.5 text-green-600 shrink-0" />
                          )}
                          {isFuzzy && (
                            <AlertTriangle
                              className="h-3.5 w-3.5 text-coral shrink-0"
                              aria-label="Fuzzy match, verify"
                            />
                          )}
                        </div>
                        <Select
                          value={current}
                          onValueChange={(v) => updateMapping(i, v)}
                        >
                          <SelectTrigger className="h-8 text-xs">
                            <SelectValue placeholder="Skip" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="skip">Skip this column</SelectItem>
                            {LEMBRA_FIELDS.map((f) => (
                              <SelectItem
                                key={f.key}
                                value={f.key}
                                disabled={mappedFields.has(f.key) && current !== f.key}
                              >
                                {f.label}
                                {f.required ? " *" : ""}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {isFuzzy && (
                          <p className="text-[10px] text-coral leading-tight">
                            Matched fuzzily to "{fuzzyMappings[i]}". Verify the data below.
                          </p>
                        )}
                      </div>
                      <div className="p-3 space-y-1 bg-charcoal/3 flex-1">
                        <p className="text-[10px] font-bold uppercase tracking-wide text-charcoal/40 mb-1">
                          Sample data
                        </p>
                        {sample.map((v, ri) => (
                          <p
                            key={ri}
                            className="text-xs text-charcoal/80 truncate"
                            title={v}
                          >
                            {v || (
                              <span className="italic text-charcoal/30">empty</span>
                            )}
                          </p>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {missingRequired.length > 0 && (
              <div className="flex items-center gap-2 text-coral text-sm">
                <AlertTriangle className="h-4 w-4" />
                <span>Required fields not mapped: {missingRequired.map((f) => f.label).join(", ")}</span>
              </div>
            )}

            <div className="flex gap-3 justify-end">
              <Button variant="outline" onClick={() => setStep("upload")}>
                Back
              </Button>
              <Button
                onClick={goToMerge}
                disabled={missingRequired.length > 0}
                className="bg-coral hover:bg-coral/90 text-white"
              >
                Continue
              </Button>
            </div>
          </div>
        )}

        {/* STEP 3: Merge analysis (NEW) */}
        {step === "merge" && analysis && (
          <div className="mt-4 space-y-4">
            {errors.length > 0 && (
              <div className="bg-coral/10 border border-coral/30 rounded-lg p-3">
                <div className="flex items-center gap-2 text-coral font-medium text-sm mb-2">
                  <AlertTriangle className="h-4 w-4" />
                  {errors.length} row{errors.length !== 1 ? "s" : ""} with missing required fields
                </div>
                <ScrollArea className="max-h-32">
                  <div className="space-y-1">
                    {errors.map((err) => (
                      <div
                        key={err.row}
                        className="flex items-center justify-between text-sm bg-white/60 rounded px-3 py-1.5"
                      >
                        <span className="text-charcoal">
                          <span className="font-medium">Row {err.row + 2}:</span> missing{" "}
                          {err.fields.join(", ")}
                        </span>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-xs h-6"
                          onClick={() => toggleSkip(err.row)}
                        >
                          {skippedRows.has(err.row) ? "Include" : "Skip"}
                        </Button>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              </div>
            )}

            <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
              <div className="rounded-lg bg-sky/15 p-3 text-center">
                <UserPlus className="h-4 w-4 mx-auto mb-1 text-navy" />
                <p className="text-xl font-bold text-navy">{analysis.toInsert.length}</p>
                <p className="text-[10px] text-charcoal/60 uppercase tracking-wide">New</p>
              </div>
              <div className="rounded-lg bg-butter/40 p-3 text-center">
                <GitMerge className="h-4 w-4 mx-auto mb-1 text-navy" />
                <p className="text-xl font-bold text-navy">{analysis.toUpdate.length}</p>
                <p className="text-[10px] text-charcoal/60 uppercase tracking-wide">Merged</p>
              </div>
              <div className="rounded-lg bg-coral/10 p-3 text-center">
                <AlertCircle className="h-4 w-4 mx-auto mb-1 text-coral" />
                <p className="text-xl font-bold text-coral">{analysis.conflicts.length}</p>
                <p className="text-[10px] text-charcoal/60 uppercase tracking-wide">Conflicts</p>
              </div>
              {/* In-CSV duplicates: rows whose email duplicates an earlier
                  row in the same upload. Surfaced separately from
                  "merge with existing" so the user knows their CSV had
                  repeats. Without this, the import could fail entirely
                  on the partial-unique-index conflict. */}
              <div className="rounded-lg bg-charcoal/8 p-3 text-center">
                <Copy className="h-4 w-4 mx-auto mb-1 text-charcoal/60" />
                <p className="text-xl font-bold text-charcoal/70">{analysis.inCsvDuplicates.length}</p>
                <p className="text-[10px] text-charcoal/60 uppercase tracking-wide">CSV dupes</p>
              </div>
              <div className="rounded-lg bg-charcoal/5 p-3 text-center">
                <Check className="h-4 w-4 mx-auto mb-1 text-charcoal/50" />
                <p className="text-xl font-bold text-charcoal/70">{analysis.unchanged}</p>
                <p className="text-[10px] text-charcoal/60 uppercase tracking-wide">Unchanged</p>
              </div>
            </div>

            {/* Safety hint: if any row in this CSV has allergy data,
                reinforce the medical-vs-preference distinction so the
                user spot-checks the column mapping before continuing.
                Catches the case where a "Dietary" column actually holds
                allergens (peanut, shellfish) that the user pasted in
                from a free-form notes field. */}
            {validMembers.some((m) => m.allergies && m.allergies.trim()) && (
              <div className="rounded-lg border border-coral/30 bg-coral/5 p-3 flex items-start gap-2">
                <ShieldAlert className="h-4 w-4 text-coral shrink-0 mt-0.5" />
                <div className="text-xs text-charcoal/80">
                  <strong className="text-coral">Allergies are medical.</strong>{" "}
                  Confirm the "Allergies" column in your CSV holds true allergies (peanut, shellfish, dairy intolerance), not preferences like vegetarian or halal. We treat them with stronger UX warnings and they're surfaced prominently on every order.
                </div>
              </div>
            )}

            {analysis.inCsvDuplicates.length > 0 && (
              <div>
                <p className="text-sm font-medium text-charcoal/70 mb-2 flex items-center gap-2">
                  <Copy className="h-4 w-4" />
                  Duplicate rows in your CSV (we'll keep the first)
                </p>
                <div className="border rounded-lg overflow-auto max-h-32">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="text-xs">Email</TableHead>
                        <TableHead className="text-xs">Dropped row</TableHead>
                        <TableHead className="text-xs">Kept row</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {analysis.inCsvDuplicates.map((d, i) => (
                        <TableRow key={`${d.email}-${d.droppedAt}-${i}`}>
                          <TableCell className="text-xs">{d.email}</TableCell>
                          <TableCell className="text-xs text-charcoal/60">Row {d.droppedAt}</TableCell>
                          <TableCell className="text-xs text-charcoal/60">Row {d.keptAt}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            )}

            {analysis.toUpdate.length > 0 && (
              <div>
                <p className="text-sm font-medium text-navy mb-2 flex items-center gap-2">
                  <GitMerge className="h-4 w-4" />
                  Filling missing data on existing records
                </p>
                <div className="border rounded-lg overflow-auto max-h-48">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Member</TableHead>
                        <TableHead>Fields filled</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {analysis.toUpdate.map((u) => (
                        <TableRow key={u.id}>
                          <TableCell className="text-sm">
                            <div className="font-medium text-navy">{u.existing.name}</div>
                            <div className="text-xs text-charcoal/60">{u.existing.email}</div>
                          </TableCell>
                          <TableCell>
                            <div className="flex gap-1 flex-wrap">
                              {u.fieldsFilled.map((f) => (
                                <span
                                  key={f}
                                  className="text-[11px] px-2 py-0.5 rounded-full bg-butter/60 text-navy"
                                >
                                  {f.replace("_", " ")}
                                </span>
                              ))}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            )}

            {analysis.conflicts.length > 0 && (
              <div>
                <div className="flex items-start justify-between gap-3 mb-2">
                  <div className="flex-1">
                    <p className="text-sm font-medium text-coral flex items-center gap-2">
                      <AlertCircle className="h-4 w-4" />
                      Conflicts, pick which value wins
                    </p>
                    <p className="text-xs text-charcoal/60 mt-1">
                      The CSV value differs from what's already on file. By default we
                      keep the existing value. Tick a row to use the CSV value instead.
                    </p>
                  </div>
                  {/* Bulk toggle. With 30 employees and 5 conflicts each,
                      one-at-a-time is 150 clicks. The bulk choices cover
                      the two common cases: "trust the CSV entirely" or
                      "I just made a mess, keep everything". */}
                  <div className="flex gap-2 shrink-0 text-xs">
                    <button
                      type="button"
                      className="text-coral hover:underline font-medium"
                      onClick={() => setAllConflictOverrides(true)}
                    >
                      Use CSV for all
                    </button>
                    <span className="text-charcoal/30">·</span>
                    <button
                      type="button"
                      className="text-charcoal/60 hover:underline font-medium"
                      onClick={() => setAllConflictOverrides(false)}
                    >
                      Keep all existing
                    </button>
                  </div>
                </div>
                <div className="border rounded-lg overflow-auto max-h-56">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-10"></TableHead>
                        <TableHead>Member</TableHead>
                        <TableHead>Field</TableHead>
                        <TableHead>Existing</TableHead>
                        <TableHead>CSV</TableHead>
                        <TableHead className="text-right">Resolution</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {analysis.conflicts.map((c) => {
                        const override = conflictOverrides.has(c.key);
                        return (
                          <TableRow key={c.key}>
                            <TableCell>
                              <input
                                type="checkbox"
                                aria-label={`Use CSV value for ${c.name}'s ${c.field}`}
                                checked={override}
                                onChange={() => toggleConflictOverride(c.key)}
                                className="h-4 w-4 accent-coral cursor-pointer"
                              />
                            </TableCell>
                            <TableCell className="text-xs font-medium">{c.name}</TableCell>
                            <TableCell className="text-xs">{c.field.replace("_", " ")}</TableCell>
                            <TableCell
                              className={`text-xs ${
                                override ? "text-charcoal/40 line-through" : "text-charcoal/70"
                              }`}
                            >
                              {c.existingValue}
                            </TableCell>
                            <TableCell
                              className={`text-xs ${
                                override ? "text-navy font-semibold" : "text-coral"
                              }`}
                            >
                              {c.csvValue}
                            </TableCell>
                            <TableCell className="text-right">
                              <span
                                className={`text-[10px] uppercase tracking-wide font-bold ${
                                  override ? "text-coral" : "text-charcoal/50"
                                }`}
                              >
                                {override ? "Use CSV" : "Keep existing"}
                              </span>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              </div>
            )}

            <div className="flex gap-3 justify-end">
              <Button variant="outline" onClick={() => setStep("map")}>
                Back
              </Button>
              <Button
                onClick={() => setStep("review")}
                disabled={analysis.toInsert.length === 0 && analysis.toUpdate.length === 0}
                className="bg-coral hover:bg-coral/90 text-white"
              >
                Continue
              </Button>
            </div>
          </div>
        )}

        {/* STEP 4: Final review (insert preview) */}
        {step === "review" && analysis && (
          <div className="mt-4 space-y-4">
            {analysis.toInsert.length > 0 && (
              <div>
                <p className="text-sm font-medium text-navy mb-2">
                  New members to add ({analysis.toInsert.length})
                </p>
                <div className="border rounded-lg overflow-auto max-h-64">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Name</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>Birthday</TableHead>
                        <TableHead>Start date</TableHead>
                        <TableHead>Department</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {analysis.toInsert.map((m, i) => (
                        <TableRow key={i}>
                          <TableCell className="font-medium">{m.name}</TableCell>
                          <TableCell>{m.email}</TableCell>
                          <TableCell>{m.birthday || "—"}</TableCell>
                          <TableCell>{m.start_date || "—"}</TableCell>
                          <TableCell>{m.department || "—"}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            )}

            {analysis.toUpdate.length > 0 && (
              <p className="text-sm text-charcoal/70">
                Plus <strong>{analysis.toUpdate.length}</strong> existing record
                {analysis.toUpdate.length !== 1 ? "s" : ""} will be updated with the missing fields
                from your CSV.
              </p>
            )}

            <div className="flex gap-3 justify-end">
              <Button variant="outline" onClick={() => setStep("merge")} disabled={importing}>
                Back
              </Button>
              <Button variant="outline" onClick={() => handleClose(false)} disabled={importing}>
                Cancel
              </Button>
              <Button
                onClick={handleImport}
                disabled={importing || (analysis.toInsert.length === 0 && analysis.toUpdate.length === 0)}
                className="bg-coral hover:bg-coral/90 text-white"
              >
                {importing
                  ? "Importing…"
                  : `Import ${analysis.toInsert.length + analysis.toUpdate.length} member${
                      analysis.toInsert.length + analysis.toUpdate.length !== 1 ? "s" : ""
                    }`}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
